import React from 'react';
import { format } from 'date-fns';
import { getDateLocale } from '../../utils/dateLocale';
import { Send, Users, AlertTriangle, CreditCard, Zap, Check, Loader2, Phone, X, Clock, ChevronRight } from 'lucide-react';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import SafePaymentForm from '../SafePaymentForm';
import api from '../../services/api';
import { toast } from 'sonner';

const SQUARE_APP_ID = process.env.REACT_APP_SQUARE_APP_ID;
const SQUARE_LOCATION_ID = process.env.REACT_APP_SQUARE_LOCATION_ID;

const OrganizerSmsSection = ({ events }) => {
  const [subscription, setSubscription] = React.useState(null);
  const [packs, setPacks] = React.useState([]);
  const [subLoading, setSubLoading] = React.useState(true);
  const [requesting, setRequesting] = React.useState(false);

  const [checkoutPack, setCheckoutPack] = React.useState(null);
  const [paying, setPaying] = React.useState(false);
  const [paymentSuccess, setPaymentSuccess] = React.useState(false);

  const [selectedEvent, setSelectedEvent] = React.useState('');
  const [smsMessage, setSmsMessage] = React.useState('');
  const [sending, setSending] = React.useState(false);
  const [history, setHistory] = React.useState([]);
  const [templates, setTemplates] = React.useState([]);
  const [recipientCount, setRecipientCount] = React.useState(null);
  const [purchases, setPurchases] = React.useState([]);

  const reload = async () => {
    try { const res = await api.get('/sms/subscription'); setSubscription(res.data.subscription); } catch {}
    try { const res = await api.get('/sms/my-purchases'); setPurchases(res.data.purchases || []); } catch {}
  };

  React.useEffect(() => {
    (async () => {
      try { const res = await api.get('/sms/subscription'); setSubscription(res.data.subscription); } catch {}
      try { const res = await api.get('/sms/packs'); setPacks(res.data.packs || []); } catch {}
      try { const res = await api.get('/sms/history'); setHistory(res.data.history || []); } catch {}
      try { const res = await api.get('/sms/templates'); setTemplates(res.data.templates || []); } catch {}
      try { const res = await api.get('/sms/my-purchases'); setPurchases(res.data.purchases || []); } catch {}
      setSubLoading(false);
    })();
  }, []);

  React.useEffect(() => {
    if (!selectedEvent) { setRecipientCount(null); return; }
    (async () => {
      try { const res = await api.get(`/sms/recipients-count/${selectedEvent}`); setRecipientCount(res.data); } catch { setRecipientCount(null); }
    })();
  }, [selectedEvent]);

  const handleRequestActivation = async () => {
    setRequesting(true);
    try {
      const res = await api.post('/sms/request-activation', { pack_id: 'sms_100' });
      setSubscription(res.data.subscription || { status: 'pending' });
      toast.success('Demande envoyee');
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
    setRequesting(false);
  };

  const handleSquarePayment = async (token) => {
    if (!checkoutPack || !token?.token) return;
    setPaying(true);
    try {
      const res = await api.post('/sms/buy-credits', {
        pack_id: checkoutPack.pack_id,
        source_id: token.token
      });
      if (res.data.success) {
        setPaymentSuccess(true);
        toast.success(res.data.message);
        reload();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur de paiement');
    }
    setPaying(false);
  };

  const closeCheckout = () => { setCheckoutPack(null); setPaymentSuccess(false); setPaying(false); };

  const handleSend = async () => {
    if (!selectedEvent || !smsMessage.trim()) return;
    setSending(true);
    try {
      const res = await api.post('/sms/send', { event_id: selectedEvent, message: smsMessage, recipients: 'all' });
      setHistory(prev => [res.data.sms, ...prev]);
      setSmsMessage('');
      toast.success(res.data.message);
      reload();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
    setSending(false);
  };

  const applyTemplate = (templateId) => {
    const tpl = templates.find(t => t.id === templateId);
    if (!tpl) return;
    const eventName = events.find(e => e.event_id === selectedEvent)?.title || 'votre evenement';
    setSmsMessage(tpl.message.replace(/{event}/g, eventName));
  };

  if (subLoading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-brand" /></div>;

  const isActive = subscription?.status === 'active';
  const isPending = subscription?.status === 'pending';

  // ==========================================
  // CHECKOUT MODAL — Square Card Payment
  // ==========================================
  const CheckoutModal = () => {
    if (!checkoutPack) return null;
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" data-testid="sms-checkout-modal">
        <div className="bg-white w-full max-w-lg mx-4 overflow-hidden" style={{ maxHeight: '90vh', overflowY: 'auto' }}>
          <div className="bg-asphalt text-white p-5 flex items-center justify-between">
            <div>
              <h3 className="font-heading font-bold text-lg uppercase">Achat de credits SMS</h3>
              <p className="text-sm text-slate-400">{checkoutPack.name}</p>
            </div>
            <button onClick={closeCheckout} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
          </div>

          {paymentSuccess ? (
            <div className="p-8 text-center" data-testid="payment-success">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="font-heading font-bold text-xl uppercase mb-2">Paiement confirme</h3>
              <p className="text-sm text-slate-600 mb-4">
                <strong>{checkoutPack.credits} credits SMS</strong> ont ete ajoutes a votre compte.
              </p>
              <Button className="bg-asphalt text-white" onClick={closeCheckout} data-testid="close-checkout-btn">Fermer</Button>
            </div>
          ) : (
            <div className="p-6 space-y-5">
              {/* Order recap */}
              <div className="bg-slate-50 border p-4">
                <p className="text-xs font-heading uppercase text-slate-500 mb-2">Votre commande</p>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-heading font-bold text-lg">{checkoutPack.credits} credits SMS</p>
                    <p className="text-xs text-slate-500">{checkoutPack.unit_price.toFixed(2)}€ par SMS</p>
                  </div>
                  <p className="font-heading font-bold text-2xl text-brand">{checkoutPack.price.toFixed(2)}€</p>
                </div>
              </div>

              {/* Square Card Form */}
              <div data-testid="square-payment-form">
                <p className="text-xs font-heading uppercase text-slate-500 mb-3">Paiement par carte bancaire</p>
                <SafePaymentForm
                  applicationId={SQUARE_APP_ID}
                  locationId={SQUARE_LOCATION_ID}
                  cardTokenizeResponseReceived={handleSquarePayment}
                  createPaymentRequest={() => ({
                    countryCode: 'FR',
                    currencyCode: 'EUR',
                    total: { amount: checkoutPack.price.toFixed(2), label: checkoutPack.name }
                  })}
                  buttonText={`Payer ${checkoutPack.price.toFixed(2)}€`}
                  buttonCss={{
                    backgroundColor: '#FF5A1F',
                    color: '#fff',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    padding: '12px',
                    width: '100%',
                    '&:hover': { backgroundColor: '#e54e18' }
                  }}
                  processing={paying}
                />
              </div>

              <div className="flex items-center justify-center gap-2 text-[10px] text-slate-400">
                <CreditCard className="w-3 h-3" />
                <span>Paiement securise via Square — Visa, Mastercard, Apple Pay</span>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // ==========================================
  // NOT ACTIVATED
  // ==========================================
  if (!isActive && !isPending) {
    return (
      <div className="space-y-6" data-testid="sms-activation-section">
        <div className="bg-gradient-to-br from-slate-900 to-slate-800 text-white p-8 rounded-lg">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-brand/20 rounded-lg flex items-center justify-center"><Phone className="w-6 h-6 text-brand" /></div>
            <div>
              <h3 className="font-heading font-bold text-xl uppercase">Notifications SMS</h3>
              <p className="text-slate-400 text-sm">Communiquez directement avec vos participants par SMS</p>
            </div>
          </div>
          <ul className="space-y-2 mb-6 text-sm text-slate-300">
            <li className="flex items-center gap-2"><Check className="w-4 h-4 text-brand" /> Rappels automatiques la veille de l'evenement</li>
            <li className="flex items-center gap-2"><Check className="w-4 h-4 text-brand" /> Notifications changement d'horaire ou meteo</li>
            <li className="flex items-center gap-2"><Check className="w-4 h-4 text-brand" /> Envoi des resultats apres la course</li>
            <li className="flex items-center gap-2"><Check className="w-4 h-4 text-brand" /> Modeles pre-rediges personnalisables</li>
          </ul>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="sms-packs">
          {packs.map((pack, i) => (
            <div key={pack.pack_id} className={`border-2 p-5 text-center ${i === 1 ? 'border-brand relative' : 'border-slate-200'}`} data-testid={`sms-pack-${pack.pack_id}`}>
              {i === 1 && <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-brand text-white text-[10px] font-heading font-bold uppercase px-3 py-0.5">Populaire</span>}
              <p className="font-heading font-bold text-3xl text-asphalt">{pack.price}€</p>
              <p className="font-heading font-bold text-sm uppercase text-slate-600 mt-1">{pack.credits} SMS</p>
              <p className="text-xs text-slate-400 mt-1">{pack.unit_price.toFixed(2)}€ / SMS</p>
            </div>
          ))}
        </div>

        <Button
          className="w-full bg-brand text-white font-heading font-bold text-sm uppercase py-6 gap-2"
          onClick={handleRequestActivation}
          disabled={requesting}
          data-testid="sms-request-activation-btn"
        >
          {requesting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
          Demander l'activation du service SMS
        </Button>
        <p className="text-xs text-center text-slate-400">L'administrateur validera votre demande. Vous pourrez ensuite acheter des credits SMS par carte bancaire.</p>
      </div>
    );
  }

  // ==========================================
  // PENDING
  // ==========================================
  if (isPending) {
    return (
      <div className="space-y-6" data-testid="sms-pending-section">
        <div className="bg-amber-50 border border-amber-200 p-6 text-center">
          <Loader2 className="w-8 h-8 text-amber-500 mx-auto mb-3 animate-spin" />
          <h3 className="font-heading font-bold text-lg uppercase text-amber-800">Demande en cours de traitement</h3>
          <p className="text-sm text-amber-600 mt-2">Votre demande d'activation du service SMS a ete envoyee. L'administrateur la traitera dans les plus brefs delais.</p>
        </div>
      </div>
    );
  }

  // ==========================================
  // ACTIVE
  // ==========================================
  return (
    <div className="space-y-6" data-testid="sms-section">
      <CheckoutModal />

      {/* Credits bar */}
      <div className="bg-white border border-slate-200 p-5" data-testid="sms-credits-bar">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 bg-brand/10 rounded-lg flex items-center justify-center"><Phone className="w-5 h-5 text-brand" /></div>
            <div>
              <p className="text-xs font-heading uppercase text-slate-500">Mes credits SMS</p>
              <p className="font-heading font-bold text-2xl text-asphalt" data-testid="sms-credits-count">{subscription?.credits || 0} <span className="text-sm font-bold text-brand">restants</span></p>
            </div>
            <div className="ml-4 pl-4 border-l">
              <p className="text-xs font-heading uppercase text-slate-500">Total envoyes</p>
              <p className="font-heading font-bold text-lg text-slate-600">{subscription?.total_sent || 0}</p>
            </div>
          </div>
        </div>

        <p className="text-xs font-heading uppercase text-slate-500 mb-3">Acheter des credits</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {packs.map((pack, i) => (
            <button
              key={pack.pack_id}
              onClick={() => { setCheckoutPack(pack); setPaymentSuccess(false); }}
              className={`border-2 p-4 text-center transition-all hover:border-brand hover:bg-brand/5 cursor-pointer ${i === 1 ? 'border-brand/50' : 'border-slate-200'}`}
              data-testid={`buy-pack-${pack.pack_id}`}
            >
              <p className="font-heading font-bold text-xl text-asphalt">{pack.price}€</p>
              <p className="font-heading font-bold text-xs uppercase text-slate-500">{pack.credits} SMS</p>
              <p className="text-[10px] text-slate-400">{pack.unit_price.toFixed(2)}€/sms</p>
              <div className="flex items-center justify-center gap-1 mt-2 text-brand text-xs font-heading font-bold">
                <CreditCard className="w-3 h-3" /> Acheter <ChevronRight className="w-3 h-3" />
              </div>
            </button>
          ))}
        </div>
      </div>

      {(subscription?.credits || 0) === 0 && (
        <div className="bg-red-50 border border-red-200 p-3 flex flex-wrap items-center gap-3" data-testid="sms-credits-empty-alert">
          <AlertTriangle className="w-4 h-4 text-red-500 shrink-0" />
          <p className="text-sm text-red-700 flex-1 min-w-[200px]">Credits epuises. Achetez un pack pour envoyer des SMS.</p>
          <button
            type="button"
            onClick={() => { const pop = packs[1] || packs[0]; if (pop) { setCheckoutPack(pop); setPaymentSuccess(false); } }}
            className="px-3 py-1.5 bg-red-600 text-white text-xs font-heading font-bold uppercase hover:bg-red-700 inline-flex items-center gap-1"
            data-testid="sms-buy-now-btn"
          >
            <CreditCard className="w-3.5 h-3.5" /> Acheter maintenant
          </button>
        </div>
      )}

      {/* Compose SMS */}
      <div className="bg-white border border-slate-200 p-5" data-testid="sms-compose">
        <h3 className="font-heading font-bold text-sm uppercase mb-2">Composer un SMS</h3>
        <div className="bg-slate-50 border border-slate-200 p-3 mb-4 text-xs text-slate-600">
          <p className="font-bold text-slate-700 mb-1">Comment sont comptes les SMS ?</p>
          <div className="grid grid-cols-2 gap-2">
            <div className="bg-white border p-2 rounded">
              <p className="font-bold text-green-700">Texte classique</p>
              <p>1 SMS = <strong>160 caracteres</strong></p>
              <p className="text-slate-400">Lettres, chiffres, ponctuation courante</p>
            </div>
            <div className="bg-white border p-2 rounded">
              <p className="font-bold text-orange-600">Avec caracteres speciaux</p>
              <p>1 SMS = <strong>70 caracteres</strong></p>
              <p className="text-slate-400">Emojis, accents rares, guillemets typographiques</p>
            </div>
          </div>
          <p className="mt-2 text-slate-500">Chaque SMS supplementaire consomme 1 credit. Maximum 320 caracteres par message.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
          <div>
            <label className="text-xs font-heading uppercase text-slate-500 mb-1 block">Evenement *</label>
            <Select value={selectedEvent} onValueChange={setSelectedEvent}>
              <SelectTrigger data-testid="sms-event-select"><SelectValue placeholder="Choisir un evenement..." /></SelectTrigger>
              <SelectContent>
                {events.map(e => <SelectItem key={e.event_id} value={e.event_id}>{e.title}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-heading uppercase text-slate-500 mb-1 block">Modele rapide</label>
            <Select onValueChange={applyTemplate}>
              <SelectTrigger data-testid="sms-template-select"><SelectValue placeholder="Choisir un modele..." /></SelectTrigger>
              <SelectContent>
                {templates.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        {recipientCount && (
          <div className="flex items-center gap-2 mb-3 text-xs">
            <Users className="w-3.5 h-3.5 text-slate-400" />
            <span className="text-slate-600">{recipientCount.total} participant(s), <strong className="text-brand">{recipientCount.with_phone}</strong> avec numero</span>
          </div>
        )}

        <textarea
          className="w-full border border-slate-200 rounded p-3 text-sm resize-none mb-2 focus:ring-1 focus:ring-brand focus:border-brand outline-none"
          rows={3}
          placeholder="Votre message SMS (160 car. = 1 SMS, 320 car. max = 2 SMS)..."
          value={smsMessage}
          onChange={(e) => { if (e.target.value.length <= 320) setSmsMessage(e.target.value); }}
          maxLength={320}
          data-testid="sms-message-input"
        />
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className={`text-xs font-mono ${smsMessage.length > 160 ? 'text-orange-500 font-bold' : smsMessage.length > 140 ? 'text-amber-500' : 'text-slate-400'}`}>
              {smsMessage.length}/320
            </span>
            <span className={`text-[10px] px-2 py-0.5 font-heading font-bold uppercase ${smsMessage.length === 0 ? 'bg-slate-100 text-slate-400' : smsMessage.length <= 160 ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'}`}>
              {smsMessage.length === 0 ? '0 SMS' : smsMessage.length <= 160 ? '1 SMS = 1 credit' : `2 SMS = 2 credits`}
            </span>
            {smsMessage.length > 160 && (
              <span className="text-[10px] text-orange-500">Segment 1: 153 car. + Segment 2: {smsMessage.length - 153} car.</span>
            )}
          </div>
          <Button
            className={`gap-2 font-heading font-bold text-xs ${(!selectedEvent || !smsMessage.trim() || (subscription?.credits || 0) === 0) ? 'bg-slate-300 text-slate-500 cursor-not-allowed hover:bg-slate-300' : 'bg-brand text-white hover:bg-brand/90'}`}
            onClick={handleSend}
            disabled={!selectedEvent || !smsMessage.trim() || sending || (subscription?.credits || 0) === 0}
            title={
              (subscription?.credits || 0) === 0 ? 'Credits SMS insuffisants — achetez un pack pour envoyer' :
              !selectedEvent ? 'Selectionnez un evenement' :
              !smsMessage.trim() ? 'Saisissez un message' : ''
            }
            data-testid="sms-send-btn"
          >
            <Send className="w-3.5 h-3.5" />
            {sending ? 'Envoi...' : (subscription?.credits || 0) === 0 ? 'Credits insuffisants' : 'Envoyer'}
          </Button>
        </div>
      </div>

      {/* History */}
      {history.length > 0 && (
        <div className="bg-white border border-slate-200" data-testid="sms-history">
          <div className="p-4 border-b"><h3 className="font-heading font-bold text-sm uppercase">Historique ({history.length})</h3></div>
          <div className="divide-y">
            {history.map(sms => (
              <div key={sms.sms_id} className="p-4" data-testid={`sms-row-${sms.sms_id}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className={`px-2 py-0.5 text-[10px] font-bold uppercase ${sms.status === 'sent' ? 'bg-green-100 text-green-700' : sms.status === 'queued' ? 'bg-yellow-100 text-yellow-700' : 'bg-slate-100 text-slate-700'}`}>
                    {sms.status === 'sent' ? 'Envoye' : sms.status === 'queued' ? 'File d\'attente' : sms.status}
                  </span>
                  <span className="text-[10px] text-slate-400">{sms.created_at && format(new Date(sms.created_at), 'd MMM yyyy HH:mm', { locale: getDateLocale() })}</span>
                </div>
                <p className="text-sm text-slate-700 bg-slate-50 p-2 rounded break-words whitespace-pre-wrap">{sms.message}</p>
                <div className="flex items-center gap-4 mt-2">
                  <span className="text-xs text-slate-400"><Users className="w-3 h-3 inline mr-1" />{sms.recipient_count} dest.</span>
                  {sms.sent_count > 0 && <span className="text-xs text-green-600">{sms.sent_count} envoye(s)</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default OrganizerSmsSection;
